import {
  Box,
  FormControl,
  Icon,
  Text,
  ToastAction,
  ToastType,
  ToggleSwitch,
  theme,
  useToastsDispatch,
} from '@bechtel/gds';
import { useAppUser } from '@bechtel/ht-clients-common';
import { ToolRoom } from '@bechtel/ht-types';
import { FC, memo, useEffect, useRef, useState } from 'react';
import { useUpdateToolRoom } from '~/hooks/use-update-tool-room';

import {
  ColumnContainer,
  SepartorStyled,
  SideView,
  TextFieldStyled,
  ToolRoomChildContainer,
  ToolRoomContainer,
  ToolRoomEditContainer,
} from './styled';

import { ToolRoomToggleView } from './toggle-view/internal';
export interface ToolRoomItemProps {
  item: ToolRoom;
  onEditClick: (x: String) => void;
  editing: boolean;
}

interface ToolRoomValues {
  lat: string;
  long: string;
  desc: string;
  transferEnabled: boolean;
  adhocEnabled: boolean;
  isActive: boolean;
}
export const ToolRoomItem: FC<ToolRoomItemProps> = memo(({ item, onEditClick, editing }) => {
  const { updateToolRoom } = useUpdateToolRoom();
  const dispatchToast = useToastsDispatch();
  const { appUser } = useAppUser();
  const [isActive, setIsActive] = useState(item.isActive);
  const valuesRef = useRef<ToolRoomValues>({
    lat: item.latitude?.toString() || '',
    long: item.longitude?.toString() || '',
    desc: item.description || '',
    transferEnabled: item.transferEnabled || false,
    adhocEnabled: item.adhocEnabled || false,
    isActive: item.isActive || false,
  });
  const setDataToRefs = () => {
    valuesRef.current = {
      lat: item.latitude?.toString() || '',
      long: item.longitude?.toString() || '',
      desc: item.description || '',
      transferEnabled: item.transferEnabled || false,
      adhocEnabled: item.adhocEnabled || false,
      isActive: item.isActive || false,
    };
    setIsActive(item.isActive);
  };
  useEffect(() => {
    setDataToRefs();
  }, [item]);
  const updateToolRoomData = (values: ToolRoom) => {
    updateToolRoom(
      values,
      (data) => {
        if (data?.updateToolRoom?.toolRoom) {
          showToast('SUCCESS', 'Tool Room Updated Successfully!');
        } else {
          showToast('ERROR', data?.updateToolRoom?.error || 'Some error occurred');
        }
      },
      (error) => {
        showToast('ERROR', error.message);
      }
    );
    onEditClick('save');
  };

  const showToast = (type: string, message: string) => {
    const updateToolRoomAction: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: type == 'ERROR' ? ToastType.DANGER : ToastType.SUCCESS,
        content: message,
        autoDismissTimeout: 3000,
      },
    };
    dispatchToast(updateToolRoomAction);
  };

  const ToolRoomEditView = () => {
    return (
      <ToolRoomEditContainer pr={{ sm: '500' }}>
        <ToolRoomChildContainer width="45%">
          <Box alignItems={{ sm: 'center' }} gap={{ sm: '400' }} style={{ width: '100%' }}>
            <SideView />
            <ColumnContainer gap={{ sm: '500' }} flexDirection={{ sm: 'column' }} width="80%">
              <Text size="400" weight="600" family="openSansSemiBold" color="neutral.800">
                {`${item.name} - ${item.barCode}`}
              </Text>

              <FormControl label="Description">
                <TextFieldStyled
                  defaultValue={item.description}
                  onChange={(event) => {
                    valuesRef.current.desc = event.target.value;
                  }}
                ></TextFieldStyled>
              </FormControl>
            </ColumnContainer>
          </Box>
          <ColumnContainer flexDirection={{ sm: 'column' }} gap={{ sm: '300' }} width="40%">
            <ToolRoomToggleView
              active={item.isActive}
              onToggle={(status) => {
                valuesRef.current.isActive = status;
              }}
              isAdhocEnabled={item.adhocEnabled || false}
              isTransferEnabled={item.transferEnabled || false}
              onTransferEnabled={(status) => {
                valuesRef.current.transferEnabled = status;
              }}
              onAdhocEnabled={(status) => {
                valuesRef.current.adhocEnabled = status;
              }}
            />
          </ColumnContainer>
        </ToolRoomChildContainer>

        <ToolRoomChildContainer width="50%">
          <ToolRoomChildContainer width="50%">
            <FormControl label="Latitude" style={{ width: '45%' }}>
              <TextFieldStyled
                defaultValue={item.latitude?.toString()}
                onChange={(event) => {
                  valuesRef.current.lat = event.target.value;
                }}
              />
            </FormControl>
            <FormControl label="Longitude" style={{ width: '45%' }}>
              <TextFieldStyled
                defaultValue={item.longitude?.toString()}
                onChange={(event) => {
                  valuesRef.current.long = event.target.value;
                }}
              />
            </FormControl>
          </ToolRoomChildContainer>
          <ToolRoomChildContainer width="25%">
            <Text
              size="300"
              weight="600"
              color="primary.700"
              onClick={() => {
                setDataToRefs();
                onEditClick('cancel');
              }}
              style={{ cursor: 'pointer' }}
            >
              Cancel
            </Text>
            <Text
              size="300"
              weight="600"
              color="primary.700"
              onClick={() => {
                updateToolRoomData({
                  id: item.id,
                  barCode: item.barCode,
                  description: valuesRef.current.desc,
                  latitude: parseFloat(valuesRef.current.lat),
                  longitude: parseFloat(valuesRef.current.long),
                  isActive: valuesRef.current.isActive,
                  thLocationId: item.thLocationId,
                  name: item.name,
                  updatedBy: appUser?.bun,
                  transferEnabled: valuesRef.current.transferEnabled,
                  adhocEnabled: valuesRef.current.adhocEnabled,
                });
                setIsActive(valuesRef.current.isActive);
              }}
              style={{ cursor: 'pointer' }}
            >
              Done
            </Text>
          </ToolRoomChildContainer>
        </ToolRoomChildContainer>
      </ToolRoomEditContainer>
    );
  };

  const ToolRoomView = () => {
    return (
      <ToolRoomContainer pr={{ sm: '500' }} mb={{ sm: '1000' }}>
        <ToolRoomChildContainer width="40%">
          <Box alignItems={{ sm: 'center' }}>
            <Box gap={{ sm: '200' }} flexDirection={{ sm: 'column' }} ml={{ sm: '400' }}>
              <Text size="400" weight="600" family="openSansSemiBold" color="neutral.800">
                {`${item.name} - ${item.barCode}`}
              </Text>
              <Text size={'300'} family="openSansRegular" color="neutral.600">
                {item.description}
              </Text>
            </Box>
          </Box>
        </ToolRoomChildContainer>
        <ToolRoomChildContainer width="35%">
          <Text size="300" color="neutral.600" family="openSansRegular">
            {`${!item.latitude ? '' : item.latitude + '° N'}  ${!item.longitude ? '' : ', ' + item.longitude + '° W'}`}
          </Text>
          <ToolRoomChildContainer width="30%">
            <Icon
              onClick={() => {
                onEditClick('cancel');
              }}
              icon="edit"
              style={{ marginRight: theme.spacings[400], cursor: 'pointer' }}
            />
            <ToggleSwitch
              isToggled={isActive}
              onToggle={() => {
                setIsActive(!isActive);
                updateToolRoomData({
                  id: item.id,
                  barCode: item.barCode,
                  description: item.description,
                  latitude: item.latitude,
                  longitude: item.longitude,
                  isActive: !isActive,
                  thLocationId: item.thLocationId,
                  name: item.name,
                  updatedBy: appUser?.bun,
                  adhocEnabled: item.adhocEnabled,
                  transferEnabled: item.transferEnabled,
                });
              }}
            />
          </ToolRoomChildContainer>
        </ToolRoomChildContainer>
      </ToolRoomContainer>
    );
  };
  return (
    <Box style={{ width: '98%' }} flexDirection={{ sm: 'column' }}>
      {editing ? <ToolRoomEditView /> : <ToolRoomView />}
      {!editing && <SepartorStyled />}
    </Box>
  );
});
